/**
 * `/blobor` (components/blobor/BloborShowcase.tsx; owner, 2026-10-31): Blobor,
 * our own game, as the platform's showcase. Every figure here comes from the
 * game's own repository: its git history and docs/PERFORMANCE.md, where each
 * before and after was measured side by side on one machine with the same
 * probe. Nothing is rounded up; a range is the spread of the runs.
 */
import type { Locale } from '../locale';

export interface ShowcaseRow {
  label: string;
  before: string;
  after: string;
}

export interface ShowcaseStory {
  id: string;
  date: string;
  title: string;
  /** What was wrong, as the players or the numbers said it. */
  problem: string;
  /** What the agents found and changed. */
  change: string;
  /** What it teaches someone who directs agents. */
  lesson: string;
  /** Measured before and after; the unit is in the label. */
  rows: ShowcaseRow[];
  measured: string;
}

const en = {
  docTitle: 'Blobor, built by AI agents · EngineerOS',
  kicker: 'Built by agents',
  title: 'A whole game, built by AI agents.',
  lede: 'Blobor is our own game: one persistent multiplayer world of blobs, live at blobor.io. Every commit in its history was made by AI agents under human direction, from the first line to the last. It is the proof behind this course: what you practise here is how Blobor was built.',
  play: 'Play Blobor',
  course: 'Start the course',
  navCourse: 'The course',
  navAgent: 'Your agent',
  shotAlt: 'A green blob on the cobbled square of Haven Outpost in Blobor, with the forest road behind it and the game\'s status card and map open',
  numbersTitle: 'The history in numbers',
  numbers: [
    { value: '1,380', label: 'commits, every one made by agents' },
    { value: '42 days', label: 'of history so far, live since its third day' },
    { value: '687', label: 'test files, written by the agents too' },
    { value: '341k', label: 'lines of TypeScript' },
  ],
  numbersNote: 'From the repository on 30 October 2026, the day of its latest commit.',
  storiesTitle: 'Before and after',
  storiesLede: 'Three changes from the history, each measured before and after on the same machine with the same probe. The agents did the work; the person directing them asked for the measurement.',
  problem: 'What was wrong',
  change: 'What the agents changed',
  lesson: 'What it teaches',
  before: 'Before',
  after: 'After',
  stories: [
    {
      id: 'arrival',
      date: '9 October 2026',
      title: 'The lag was the shaders, not the building',
      problem: 'Players said the game "sometimes lags": one frame of up to several seconds whenever they arrived somewhere new. The first explanation on record blamed building the world around the player.',
      change: 'A profile of the seconds after each teleport said otherwise: 95% of the long frame was the first use of one shader program, and building everything around the player was about 100 ms of it. The agents asked every program its questions once, before drawing, and put a hidden specimen of every creature in the scene at the join so its shaders were ready.',
      lesson: 'An agent\'s first diagnosis is a hypothesis. Ask for the profile before the fix, and the fix lands where the time actually goes.',
      rows: [
        { label: 'Longest frame on arrival, town', before: '498–5,789 ms', after: '107–264 ms' },
        { label: 'Whisperwood', before: '860–1,973 ms', after: '22–48 ms' },
        { label: 'Frostpine', before: '849–1,330 ms', after: '56–70 ms' },
      ],
      measured: 'Three runs a side, alternating, on one machine (tests/perf/frame-probe.mjs).',
    },
    {
      id: 'first-draw',
      date: '9 October 2026',
      title: 'The first frame: from 1.4 seconds to 0.2',
      problem: 'The longest task in joining the world was the very first draw: about thirty shader programs compiled one after another while the player waited.',
      change: 'The agents compiled the scene in parallel before the world answered (KHR_parallel_shader_compile) and held back only drawing until it was ready. In the same pass, the fruit batches stopped re-sending every slot every frame and sent only what is drawn.',
      lesson: 'Name the number you want moved. "Make loading faster" invites guesses; "the first world draw takes 1.4 s" gets a measured answer.',
      rows: [
        { label: 'First world draw', before: '1,422–1,464 ms', after: '188–281 ms' },
        { label: 'Fruit upload per frame', before: '324 KB', after: '63.5 KB' },
      ],
      measured: 'Side by side on one machine with the frame probe.',
    },
    {
      id: 'sweep',
      date: '21 October 2026',
      title: 'An audit that argued with itself',
      problem: 'Frames on phones and slower laptops were uneven, and nobody knew which of many small costs mattered.',
      change: 'A read-only audit by agents across nine areas, with every finding checked by a second reader trying to refute it: 42 of 61 findings survived. The profile then found the biggest cost the audit missed: walking 8,600 objects every frame, 5,700 of which never move. Those now rest until something moves them.',
      lesson: 'Have one agent try to refute another\'s findings before anything is changed, then measure on the device that matters, not the one you develop on.',
      rows: [
        { label: 'Phone, frames per second', before: '18.0–18.8', after: '26.2–29.7' },
        { label: 'Phone, frames over 50 ms', before: '137–211', after: '48–58' },
        { label: 'Laptop (lowest quality), frames per second', before: '31.9', after: '45.9' },
      ],
      measured: 'A/B on one machine, 40 players in the plaza, the CPU throttled to stand in for a laptop (4x) and a phone (6x).',
    },
  ] as ShowcaseStory[],
  /** The landing page's band (components/blobor/BloborBand.tsx). */
  band: {
    label: 'Built by agents',
    title: 'A whole game, built by AI agents.',
    lede: 'Blobor, our own multiplayer game, was built entirely by AI agents under human direction: 1,380 commits in six weeks, live at blobor.io. Its history is in this course, as real changes you redo with your own agent.',
    link: 'See how it was built',
  },
  nextTitle: 'Your turn, on the same history',
  nextBody: 'Because we have the whole history, we know how each of these changes really went: the code before, the change, and its measurement. That makes them assessments with a known answer. You get the code as it was, brief your own agent, and your result is checked against what the real change achieved.',
  nextCta: 'Try one in the course',
};

export type BloborCopy = typeof en;

export const bloborCopy: Record<Locale, BloborCopy> = {
  en,
  // English only for now (owner, 2026-10-29).
  ar: en,
  // English only for now (owner, 2026-10-29).
  de: en,
  // English only for now (owner, 2026-10-29).
  ja: en,
};

/** The game, with the tag Blobor's own stats read (utm_source on the new player, utm_campaign on the session). */
export function bloborShowcaseLink(): string {
  const url = new URL('https://blobor.io');
  url.searchParams.set('utm_source', 'engineeros');
  url.searchParams.set('utm_campaign', 'showcase');
  return url.toString();
}
